import { Box, Grid, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import WizardArchTypeChip from 'pages/GettingStarted/Wizard/components/WizardArchTypeChip';
import WizardBackButton from 'pages/GettingStarted/Wizard/components/WizardBackLink';
import WizardCard from 'pages/GettingStarted/Wizard/components/WizardCard';
import WizardPrimaryButton from 'pages/GettingStarted/Wizard/components/WizardPrimaryButton';
import WizardContentLayout from 'pages/GettingStarted/Wizard/Phases/WizardContentLayout';
import {
	PRIMARY_METHODS_BTB,
	PRIMARY_METHODS_BTC,
	WIZARD_PAGES
} from 'pages/GettingStarted/Wizard/utils/consts';
import React, { FC } from 'react';
import { ArchType, IWizardProps, WizardAuthMethod } from 'types/wizard';
import { useWizardUpdate } from '../../utils/hook';

const PrimaryMethods: FC<IWizardProps> = ({ state }) => {
	const { update } = useWizardUpdate(state);
	const { archType, primaryMethods } = state;

	const [selected, setSelected] = React.useState(primaryMethods || []);

	const handleNext = React.useCallback(() => {
		update({
			primaryMethods: selected,
			page: WIZARD_PAGES.secondaryMethod
		});
	}, [update, selected]);

	const handleBack = React.useCallback(() => {
		update({
			page: WIZARD_PAGES.archType
		});
	}, [update]);

	const handleClick = React.useCallback(
		(method: WizardAuthMethod) => {
			if (selected.includes(method)) {
				setSelected(selected.filter((item) => item !== method));
			} else if (selected.length < 2) {
				setSelected([...selected, method]);
			}
		},
		[selected]
	);

	const methods =
		archType === ArchType.B2C ? PRIMARY_METHODS_BTC : PRIMARY_METHODS_BTB;

	const shouldDisabled = selected.length === 2;

	return (
		<WizardContentLayout
			{...{ state }}
			backButton={<WizardBackButton onClick={handleBack} />}
			actionButton={
				<WizardPrimaryButton
					disabled={isEmpty(selected)}
					onClick={handleNext}
				/>
			}
		>
			<Box
				display="flex"
				flexDirection="column"
				flexGrow={1}
				data-testid="wizard-primary-methods"
			>
				<Box
					display="flex"
					flexDirection={{
						xs: 'column',
						sm: 'row'
					}}
				>
					<Box
						display="flex"
						flexDirection={{
							xs: 'row',
							sm: 'column'
						}}
						data-testid="wizard-primary-methods-chip"
					>
						<WizardArchTypeChip archType={archType} />
					</Box>
					<Box
						display="flex"
						flexDirection="column"
						flexGrow={1}
						pl={{
							xs: 0,
							sm: 2
						}}
					>
						<Typography variant="h6">
							{intl.tr('wizard.phase.build.primaryMethods.label')}
						</Typography>
						<Typography variant="subtitle1">
							{intl.tr('wizard.phase.build.primaryMethods.label.secondary')}
						</Typography>
					</Box>
				</Box>
				<Grid
					container
					display="flex"
					flexGrow={1}
					justifyContent="flex-start"
					pt={4}
					pb={1}
					pl={1}
				>
					<Grid item xs={12}>
						<Box display="flex" flexDirection="row" flexGrow={1}>
							<Grid container spacing={4}>
								{methods.map((method) => {
									// make sure enchanted link and magic link are not selected together
									const authDisabled =
										shouldDisabled ||
										(method.value === WizardAuthMethod.MagicLink &&
											selected.includes(WizardAuthMethod.EnchantedLink)) ||
										(method.value === WizardAuthMethod.EnchantedLink &&
											selected.includes(WizardAuthMethod.MagicLink));
									return (
										<Grid
											item
											key={method.value}
											pt={3}
											data-testid={`wizard-primary-methods-card-${method.value}`}
										>
											<WizardCard
												cardTag={intl.tr('wizard.phase.build.card.tag.primary')}
												size="small"
												disabled={
													authDisabled && !selected.includes(method.value)
												}
												label={intl.tr(method.label)}
												Icon={method.icon}
												selected={(selected || []).includes(method.value)}
												onClick={() => handleClick(method.value)}
												subtitle={
													method.subtitle
														? intl.tr(`wizard.methods.${method.value}`)
														: ''
												}
											/>
										</Grid>
									);
								})}
							</Grid>
						</Box>
					</Grid>
				</Grid>
			</Box>
		</WizardContentLayout>
	);
};

export default PrimaryMethods;
